/**
 * The pane chunk's one import (1.I-2 deliverables 4 and 27): `PaneDocument` and the editor behind
 * a dynamic import, so they stay out of the root chunk. The warm-up on a chip's `pointerdown`,
 * `open()` and `LazyPaneDocument` all go through `loadPaneModule`, so the module is fetched and
 * resolved once; a failed import is forgotten, so the next open tries again.
 *
 * `setPaneImporter` is the seam the harness counts and holds the import through (specs only).
 */
import type * as PaneDocumentModule from './pane-document.js';

export type PaneModule = typeof PaneDocumentModule;
export type PaneImporter = () => Promise<PaneModule>;

const importPane: PaneImporter = () => import('./pane-document.js');

let importer: PaneImporter = importPane;
let loading: Promise<PaneModule> | null = null;
let loaded: PaneModule | null = null;

/** Starts the import once (or joins the one under way) and answers the module. */
export function loadPaneModule(): Promise<PaneModule> {
  if (loading === null) {
    const started = importer().then(
      (module) => {
        loaded = module;
        return module;
      },
      (error: unknown) => {
        if (loading === started) loading = null;
        throw error;
      },
    );
    loading = started;
  }
  return loading;
}

/** The module once it has resolved; null before. */
export function loadedPaneModule(): PaneModule | null {
  return loaded;
}

/** Replaces the import (specs: count it, hold it); null restores the real one. Forgets the module. */
export function setPaneImporter(next: PaneImporter | null): void {
  importer = next ?? importPane;
  loading = null;
  loaded = null;
}
